import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Loading, EmptyState, StatusBadge } from "@/components/common";
import { numberID } from "@/lib/format";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Layers, Search, ChevronDown, ChevronRight, CheckCircle2, AlertTriangle } from "lucide-react";

const COLS = [
  ["cutting", "Cutting"], ["sewing", "Sewing"], ["finishing", "Finishing"],
  ["qc_masuk", "QC Masuk"], ["qc_checked", "QC Diperiksa"], ["qc_passed", "Passed"],
  ["qc_reject", "Reject"], ["rework", "Rework"], ["scrap", "Scrap"],
];

function QcQueueBadge({ po }) {
  if (!po.qc_queue_available) return <span className="text-xs text-slate-400" data-testid="qc-queue-na">N/A</span>;
  const n = po.qc_queue_actual;
  const cls = n > 0 ? "bg-amber-50 text-amber-700" : "bg-emerald-50 text-emerald-700";
  return (
    <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${cls}`} data-testid={`qc-queue-${po.po_prod_no}`}>
      {n > 0 ? `Antri: ${numberID(n)}` : "Kosong"}
    </span>
  );
}

export default function ProductionBreakdown({ period, start_date, end_date }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [qInput, setQInput] = useState("");
  const [q, setQ] = useState("");
  const [open, setOpen] = useState({});

  const params = { period, page, page_size: 20 };
  if (period === "custom") { params.start_date = start_date; params.end_date = end_date; }
  if (q) params.q = q;
  const key = JSON.stringify(params);

  useEffect(() => {
    if (period === "custom" && (!start_date || !end_date)) { setLoading(false); return; }
    setLoading(true);
    api.get("/dashboard/production-breakdown", { params })
      .then((r) => setData(r.data)).catch(() => setData(null)).finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  // reset to page 1 when period/search changes
  useEffect(() => { setPage(1); /* eslint-disable-next-line */ }, [period, start_date, end_date, q]);

  const rec = data?.reconciliation;
  const tot = data?.totals;
  const pg = data?.pagination;

  return (
    <div className="mt-8" data-testid="prod-breakdown-section">
      <div className="mb-1 flex items-center gap-2">
        <Layers className="h-4 w-4 text-indigo-600" />
        <h3 className="font-display text-lg font-semibold text-slate-900">Breakdown Produksi per PO & Produk</h3>
        <span className="rounded-full bg-sky-50 px-2 py-0.5 text-[11px] font-semibold text-sky-600">Aktivitas periode (WIB)</span>
      </div>
      <p className="mb-3 text-xs text-slate-400">
        Angka di tabel ini adalah <b>aktivitas dalam periode terpilih</b> (ditelusuri dari transaksi produksi), berbeda dari
        kolom <b>Target</b> & antrean QC yang merupakan <b>progres/snapshot kumulatif PO</b> saat ini.
      </p>

      {/* Reconciliation banner */}
      {rec && (
        <div className={`mb-3 rounded-xl border p-3 text-xs ${rec.reconciled ? "border-emerald-200 bg-emerald-50" : "border-amber-200 bg-amber-50"}`} data-testid="breakdown-reconcile">
          <div className="flex items-center gap-2 font-semibold">
            {rec.reconciled ? <CheckCircle2 className="h-4 w-4 text-emerald-600" /> : <AlertTriangle className="h-4 w-4 text-amber-600" />}
            <span className={rec.reconciled ? "text-emerald-700" : "text-amber-700"}>
              {rec.reconciled ? "Total breakdown terekonsiliasi dengan KPI dashboard." : "Perhatian: total breakdown belum sama dengan KPI."}
            </span>
          </div>
          {rec.unattributed?.present && (
            <p className="mt-1 text-slate-600" data-testid="breakdown-unattr">
              Tidak ter-atribusi ke PO (legacy/koreksi tanpa atribusi, tidak dialokasikan secara asumsi):
              {[["cutting", "Cutting"], ["sewing", "Sewing"], ["finishing", "Finishing"], ["qc_reject", "Reject"], ["rework", "Rework"], ["scrap", "Scrap"]]
                .filter(([f]) => rec.unattributed[f]).map(([f, l]) => ` ${l} ${numberID(rec.unattributed[f])}`).join(",") || " —"}
              {(rec.unattributed.kg_used || rec.unattributed.waste_kg) ? ` · Kain ${numberID(rec.unattributed.kg_used)} kg (waste ${numberID(rec.unattributed.waste_kg)} kg)` : ""}
            </p>
          )}
          {(rec.notes || []).map((n, i) => <p key={i} className="mt-1 text-slate-500">• {n}</p>)}
        </div>
      )}

      {/* Search */}
      <div className="mb-3 flex items-center gap-2">
        <div className="relative">
          <Search className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
          <input
            value={qInput}
            onChange={(e) => setQInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") setQ(qInput.trim()); }}
            placeholder="Cari No PO / produk / SKU..."
            className="w-72 rounded-lg border border-slate-300 py-1.5 pl-8 pr-3 text-sm"
            data-testid="breakdown-search-input"
          />
        </div>
        <button onClick={() => setQ(qInput.trim())} className="rounded-lg bg-indigo-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-indigo-700" data-testid="breakdown-search-btn">Cari</button>
        {q && <button onClick={() => { setQInput(""); setQ(""); }} className="text-xs text-slate-500 underline" data-testid="breakdown-search-clear">Reset</button>}
      </div>

      {loading ? <Loading label="Memuat breakdown..." /> : !data || data.pos.length === 0 ? (
        <EmptyState title={q ? "Tidak ada PO yang cocok dengan pencarian" : "Tidak ada aktivitas produksi pada periode ini"} icon={Layers} />
      ) : (
        <>
          <div className="rounded-xl border border-slate-200 bg-white shadow-sm overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-6"></TableHead>
                  <TableHead>No PO / Produk</TableHead>
                  <TableHead className="text-right">Target</TableHead>
                  {COLS.map(([f, l]) => <TableHead key={f} className="text-right">{l}</TableHead>)}
                  <TableHead className="text-right">Kain (kg)</TableHead>
                  <TableHead className="text-center">Antrean QC</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.pos.map((po) => (
                  <React.Fragment key={po.production_order_id}>
                    <TableRow className="cursor-pointer hover:bg-slate-50" onClick={() => setOpen((o) => ({ ...o, [po.production_order_id]: !o[po.production_order_id] }))} data-testid={`breakdown-row-${po.po_prod_no}`}>
                      <TableCell>{open[po.production_order_id] ? <ChevronDown className="h-4 w-4 text-slate-400" /> : <ChevronRight className="h-4 w-4 text-slate-400" />}</TableCell>
                      <TableCell>
                        <div className="font-mono text-xs font-semibold">{po.po_prod_no}</div>
                        <div className="flex items-center gap-1.5 text-xs text-slate-500">
                          <span className="h-2.5 w-2.5 rounded-full border border-slate-300" style={{ background: po.color_hex }} />
                          {po.product_name} · {po.color_name}
                          {po.corrections_included && <span className="rounded bg-amber-50 px-1 text-[10px] font-semibold text-amber-600">+koreksi</span>}
                        </div>
                      </TableCell>
                      <TableCell className="text-right text-slate-500">{numberID(po.target)}</TableCell>
                      {COLS.map(([f]) => <TableCell key={f} className="text-right">{numberID(po[f])}</TableCell>)}
                      <TableCell className="text-right">{po.kg_attributed ? `${numberID(po.kg_used)}` : <span className="text-slate-300">N/A</span>}</TableCell>
                      <TableCell className="text-center"><QcQueueBadge po={po} /></TableCell>
                      <TableCell><StatusBadge status={po.status} /></TableCell>
                    </TableRow>
                    {open[po.production_order_id] && (
                      <TableRow data-testid={`breakdown-items-${po.po_prod_no}`}>
                        <TableCell colSpan={COLS.length + 5} className="bg-slate-50/60 p-0">
                          <div className="px-6 py-3">
                            <div className="mb-2 text-xs font-semibold text-slate-500">Rincian per Produk</div>
                            <Table>
                              <TableHeader>
                                <TableRow>
                                  <TableHead>SKU / Varian</TableHead>
                                  <TableHead className="text-right">Target</TableHead>
                                  {COLS.map(([f, l]) => <TableHead key={f} className="text-right">{l}</TableHead>)}
                                  <TableHead className="text-right">Antrean QC</TableHead>
                                </TableRow>
                              </TableHeader>
                              <TableBody>
                                {po.items.map((it) => (
                                  <TableRow key={it.item_id} data-testid={`breakdown-item-${it.sku}`}>
                                    <TableCell className="text-xs"><span className="font-mono font-semibold">{it.sku}</span><span className="ml-1 text-slate-400">{it.variant}</span></TableCell>
                                    <TableCell className="text-right text-slate-500">{numberID(it.target)}</TableCell>
                                    {COLS.map(([f]) => <TableCell key={f} className="text-right">{numberID(it[f])}</TableCell>)}
                                    <TableCell className="text-right text-slate-500">{numberID(it.qc_queue_actual)}</TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </div>
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                ))}
                {/* Totals row */}
                {tot && (
                  <TableRow className="border-t-2 border-slate-200 bg-slate-50 font-semibold" data-testid="breakdown-totals">
                    <TableCell></TableCell>
                    <TableCell className="text-xs text-slate-600">TOTAL PERIODE (semua PO)</TableCell>
                    <TableCell></TableCell>
                    {COLS.map(([f]) => <TableCell key={f} className="text-right">{numberID(tot[f])}</TableCell>)}
                    <TableCell className="text-right">{numberID(tot.kg_used)}</TableCell>
                    <TableCell></TableCell>
                    <TableCell></TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>

          {/* Pagination */}
          {pg && pg.total_pages > 1 && (
            <div className="mt-3 flex items-center justify-between text-sm">
              <span className="text-slate-500">{pg.total_pos} PO · Halaman {pg.page}/{pg.total_pages}</span>
              <div className="flex gap-2">
                <button disabled={pg.page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))} className="rounded-lg border border-slate-300 px-3 py-1 disabled:opacity-40" data-testid="breakdown-prev">Sebelumnya</button>
                <button disabled={pg.page >= pg.total_pages} onClick={() => setPage((p) => p + 1)} className="rounded-lg border border-slate-300 px-3 py-1 disabled:opacity-40" data-testid="breakdown-next">Berikutnya</button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
